import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { api } from '../lib/api'
import ClaimReceiptTypePick from '../components/ClaimReceiptTypePick'
import ManagedCustomerAutocomplete from '../components/ManagedCustomerAutocomplete'
import ClaimAdditionalFieldInputs from '../components/claim/ClaimAdditionalFieldInputs'
import { todayYmdLocal } from '../lib/claimContactFormat'
import { PhoneDigitsSplitInput } from '../components/claimSplitInputs'
import {
  isAdditionalFieldVisible,
  normalizeAdditionalFieldDefinitions,
  validateRequiredAdditionalFields,
} from '../lib/claimAdditionalFields'
import {
  CLAIM_MAX_DOCUMENT_PAGE_COUNT,
  claimDocumentPageCount,
  claimDocumentsPageCount,
  prepareClaimDocumentFiles,
  takeClaimDocumentsWithinPageLimit,
} from '../lib/claimDocumentPages'

const CLAIM_MAX_DOCUMENT_COUNT = CLAIM_MAX_DOCUMENT_PAGE_COUNT

function normalizeClaimDocumentsForForm(documents, fallbackType = 'ETC') {
  const rows = Array.isArray(documents) ? documents : []
  return takeClaimDocumentsWithinPageLimit(
    rows.map((doc, idx) => ({
      fileId: doc?.fileId,
      documentType: doc?.documentType || fallbackType,
      label: doc?.label || doc?.originalName || `첨부 ${idx + 1}`,
      sortOrder: doc?.sortOrder ?? idx,
      pageCount: claimDocumentPageCount(doc),
    })),
    CLAIM_MAX_DOCUMENT_COUNT,
  )
}

function StepSection({ step, title, open, onToggle, children }) {
  return (
    <div className="rounded border bg-white">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-slate-500">STEP {step}</span>
          <span className="font-semibold text-slate-900">{title}</span>
        </div>
        <span className="text-slate-400">{open ? '▼' : '▶'}</span>
      </button>
      {open && <div className="border-t p-4">{children}</div>}
    </div>
  )
}

export function ClaimApplicationForm({
  embedded = false,
  draftApplicationId = null,
  initialInsurerOrgId = null,
  initialCategoryRootOrgId = null,
  initialCustomerRefId = null,
  initialCustomerName = '',
  initialCustomerBirthDate = '',
  initialCustomerPhone = '',
  initialClaimSection = '',
  initialClaimItem = '',
  initialClaimAmount = '',
  initialClaimDate = '',
  initialAccidentDate = '',
  initialMemo = '',
  initialExtraFields = null,
  initialDocuments = [],
  lockInsurer = false,
  lockCategory = false,
  lockCommonFields = false,
  onSaved,
  onCancel,
}) {
  const navigate = useNavigate()
  const [effectiveDraftId, setEffectiveDraftId] = useState(
    draftApplicationId != null && draftApplicationId > 0 ? draftApplicationId : null
  )
  const [loadingDraft, setLoadingDraft] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitMode, setSubmitMode] = useState('SUBMITTED')
  const [error, setError] = useState(null)
  const [insurerOrgs, setInsurerOrgs] = useState([])
  const [claimTemplateByOrgId, setClaimTemplateByOrgId] = useState({})
  const [claimTemplateLoading, setClaimTemplateLoading] = useState(false)
  const [categories, setCategories] = useState([])
  const [guides, setGuides] = useState([])
  const [openStep, setOpenStep] = useState('1')
  const [additionalFieldDefs, setAdditionalFieldDefs] = useState([])
  const [additionalFieldValues, setAdditionalFieldValues] = useState({})
  const [persistedExtraFields, setPersistedExtraFields] = useState(
    initialExtraFields && typeof initialExtraFields === 'object' ? initialExtraFields : null
  )

  const [form, setForm] = useState({
    customerRefId: initialCustomerRefId ? String(initialCustomerRefId) : '',
    customerName: initialCustomerName || '',
    customerBirthDate: initialCustomerBirthDate || '',
    customerPhone: initialCustomerPhone || '',
    insurerOrgId: initialInsurerOrgId ? Number(initialInsurerOrgId) : null,
    categoryRootOrgId: initialCategoryRootOrgId ? Number(initialCategoryRootOrgId) : null,
    claimSection: initialClaimSection || '',
    claimItem: initialClaimItem || '',
    claimAmount: initialClaimAmount || '',
    claimDate: initialClaimDate || todayYmdLocal(),
    accidentDate: initialAccidentDate || '',
    claimContent: '',
    autoInsuranceProcessed: '',
    /** extraFields.accidentReceiptType: NEW | ADDITIONAL */
    accidentReceiptType: 'NEW',
    memo: initialMemo || '',
    documents: normalizeClaimDocumentsForForm(initialDocuments),
  })

  const formRef = useRef(form)
  formRef.current = form
  const effectiveDraftIdRef = useRef(
    draftApplicationId != null && draftApplicationId > 0 ? draftApplicationId : null
  )
  const lastSilentSaveHashRef = useRef('')
  const prevOpenStepRef = useRef(openStep)
  const silentSaveLockRef = useRef(false)

  effectiveDraftIdRef.current = effectiveDraftId

  useEffect(() => {
    const next = draftApplicationId != null && draftApplicationId > 0 ? draftApplicationId : null
    setEffectiveDraftId(next)
  }, [draftApplicationId])

  useEffect(() => {
    loadInsurerOrgs()
    loadCategories()
  }, [])

  useEffect(() => {
    if (!insurerOrgs.length) {
      setClaimTemplateByOrgId({})
      setClaimTemplateLoading(false)
      return
    }
    let cancelled = false
    setClaimTemplateLoading(true)
    const q = new URLSearchParams()
    insurerOrgs.forEach((o) => q.append('insurerOrgIds', o.id))
    ;(async () => {
      try {
        const res = await api.get(`/claims/form-templates/statuses?${q.toString()}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const rows = Array.isArray(payload) ? payload : []
        if (cancelled) return
        const next = {}
        for (const row of rows) {
          const oid = row?.insurerOrgId
          if (oid == null) continue
          next[String(oid)] = !!row.hasTemplate
        }
        for (const o of insurerOrgs) {
          const k = String(o.id)
          if (!(k in next)) next[k] = false
        }
        setClaimTemplateByOrgId(next)
      } catch {
        if (!cancelled) {
          const fallback = {}
          insurerOrgs.forEach((o) => { fallback[String(o.id)] = true })
          setClaimTemplateByOrgId(fallback)
          setError('청구 서식 등록 여부를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.')
        }
      } finally {
        if (!cancelled) setClaimTemplateLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [insurerOrgs])

  useEffect(() => {
    if (claimTemplateLoading || loadingDraft) return
    if (lockInsurer) return
    if (draftApplicationId) return
    const id = formRef.current.insurerOrgId
    if (!id) return
    if (claimTemplateByOrgId[String(id)] === false) {
      setForm((prev) => ({
        ...prev,
        insurerOrgId: null,
        categoryRootOrgId: null,
        claimSection: '',
        claimItem: '',
      }))
      setError('선택한 보험사에 등록된 청구 서식이 없습니다. 운영 > 서식관리 > 청구서식에서 등록한 뒤 다시 선택해 주세요.')
    }
  }, [claimTemplateByOrgId, claimTemplateLoading, loadingDraft, draftApplicationId, lockInsurer])

  useEffect(() => {
    if (initialExtraFields && typeof initialExtraFields === 'object') {
      setPersistedExtraFields(initialExtraFields)
      if (lockCommonFields) {
        const x = initialExtraFields
        const art = x.accidentReceiptType === 'ADDITIONAL' || x.accidentReceiptType === 'NEW' ? x.accidentReceiptType : null
        setForm((prev) => ({
          ...prev,
          claimContent: String(x.claimContent ?? prev.claimContent ?? ''),
          autoInsuranceProcessed: String(x.autoInsuranceProcessed ?? prev.autoInsuranceProcessed ?? ''),
          ...(art ? { accidentReceiptType: art } : {}),
        }))
      }
    }
  }, [initialExtraFields, lockCommonFields])

  useEffect(() => {
    if (!draftApplicationId) return
    let ignore = false
    ;(async () => {
      setLoadingDraft(true)
      setError(null)
      try {
        const res = await api.get(`/claims/applications/${draftApplicationId}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!payload || ignore) return
        let parsedExtraFields = null
        try {
          parsedExtraFields = payload?.extraFieldsJson ? JSON.parse(payload.extraFieldsJson) : null
        } catch {
          parsedExtraFields = null
        }
        setPersistedExtraFields(parsedExtraFields)
        const ex = parsedExtraFields && typeof parsedExtraFields === 'object' ? parsedExtraFields : {}
        const art = ex.accidentReceiptType === 'ADDITIONAL' || ex.accidentReceiptType === 'NEW' ? ex.accidentReceiptType : 'NEW'
        setForm({
          customerRefId: payload.customerRefId ? String(payload.customerRefId) : '',
          customerName: payload.customerName || '',
          customerBirthDate: payload.customerBirthDate || '',
          customerPhone: payload.customerPhone || '',
          insurerOrgId: payload.insurerOrgId ? Number(payload.insurerOrgId) : null,
          categoryRootOrgId: payload.categoryRootOrgId ? Number(payload.categoryRootOrgId) : null,
          claimSection: payload.claimSection || '',
          claimItem: payload.claimItem || '',
          claimAmount: payload.claimAmount != null ? String(payload.claimAmount) : '',
          claimDate:
            payload.claimDate != null && String(payload.claimDate).trim() !== ''
              ? String(payload.claimDate).slice(0, 10)
              : todayYmdLocal(),
          accidentDate: payload.accidentDate || '',
          claimContent: String(ex.claimContent || ''),
          autoInsuranceProcessed: String(ex.autoInsuranceProcessed || ''),
          accidentReceiptType: art,
          memo: payload.memo || '',
          documents: normalizeClaimDocumentsForForm(payload.documents),
        })
        lastSilentSaveHashRef.current = ''
        setOpenStep(lockCommonFields ? '2' : '3')
      } catch {
        if (!ignore) {
          setError('임시저장 청구서를 불러오지 못했습니다.')
        }
      } finally {
        if (!ignore) setLoadingDraft(false)
      }
    })()
    return () => { ignore = true }
  }, [draftApplicationId, lockCommonFields])

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      customerRefId: initialCustomerRefId ? String(initialCustomerRefId) : prev.customerRefId,
      customerName: initialCustomerName || prev.customerName,
      customerBirthDate: initialCustomerBirthDate || prev.customerBirthDate,
      customerPhone: initialCustomerPhone || prev.customerPhone,
      insurerOrgId: initialInsurerOrgId ? Number(initialInsurerOrgId) : prev.insurerOrgId,
      categoryRootOrgId: initialCategoryRootOrgId ? Number(initialCategoryRootOrgId) : prev.categoryRootOrgId,
      claimSection: initialClaimSection || prev.claimSection,
      claimItem: initialClaimItem || prev.claimItem,
      claimAmount: initialClaimAmount || prev.claimAmount,
      claimDate: (initialClaimDate != null && String(initialClaimDate).trim() !== '')
        ? initialClaimDate
        : (prev.claimDate || todayYmdLocal()),
      accidentDate: initialAccidentDate || prev.accidentDate,
      memo: initialMemo || prev.memo,
      documents: prev.documents.length > 0
        ? normalizeClaimDocumentsForForm(prev.documents)
        : normalizeClaimDocumentsForForm(initialDocuments),
    }))
  }, [
    initialCustomerRefId,
    initialCustomerName,
    initialCustomerBirthDate,
    initialCustomerPhone,
    initialInsurerOrgId,
    initialCategoryRootOrgId,
    initialClaimSection,
    initialClaimItem,
    initialClaimAmount,
    initialClaimDate,
    initialAccidentDate,
    initialMemo,
    initialDocuments,
  ])

  useEffect(() => {
    if (!form.categoryRootOrgId) {
      setGuides([])
      return
    }
    loadGuides()
  }, [form.categoryRootOrgId])

  useEffect(() => {
    if (!form.insurerOrgId) {
      setAdditionalFieldDefs([])
      setAdditionalFieldValues({})
      return undefined
    }
    let ignore = false
    setAdditionalFieldValues({})
    ;(async () => {
      try {
        const res = await api.get('/claims/additional-fields', {
          params: { insurerOrgId: form.insurerOrgId },
        })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const defs = normalizeAdditionalFieldDefinitions(payload)
        if (ignore) return
        setAdditionalFieldDefs(defs)
        setAdditionalFieldValues((prev) => {
          const persisted = persistedExtraFields && typeof persistedExtraFields === 'object' ? persistedExtraFields : {}
          const next = { ...prev }
          defs.forEach((field) => {
            if (next[field.fieldKey] == null && persisted[field.fieldKey] != null) {
              next[field.fieldKey] = String(persisted[field.fieldKey])
            }
          })
          return next
        })
      } catch {
        if (!ignore) {
          setAdditionalFieldDefs([])
        }
      }
    })()
    return () => { ignore = true }
  }, [form.insurerOrgId, persistedExtraFields])

  useEffect(() => {
    if (form.insurerOrgId) {
      setOpenStep('2')
    }
  }, [form.insurerOrgId])

  useEffect(() => {
    if (lockCommonFields) return
    if (form.customerName && form.insurerOrgId) {
      setOpenStep('3')
    }
  }, [form.customerName, form.insurerOrgId, lockCommonFields])

  const guideOptions = useMemo(() => {
    return Array.isArray(guides) ? guides : []
  }, [guides])

  const isLocked = (value) => lockCommonFields && value != null && String(value).trim() !== ''

  const loadInsurerOrgs = async () => {
    try {
      const res = await api.get('/orgs', { params: { type: 'INSURER', parentId: null } })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const items = Array.isArray(payload?.items) ? payload.items : (Array.isArray(payload) ? payload : [])
      const byId = new Map(items.map((o) => [o.id, o]))
      const companyLevel = items.filter((o) => {
        if (!o || o.type !== 'INSURER' || o.parentOrgId == null) return false
        const parent = byId.get(o.parentOrgId)
        return !!(parent && parent.type === 'INSURER' && parent.parentOrgId == null)
      })
      const insurers = companyLevel.length > 0
        ? companyLevel
        : items.filter((o) => o && o.type === 'INSURER')
      setInsurerOrgs(insurers)
    } catch {
      setInsurerOrgs([])
    }
  }

  const loadCategories = async () => {
    try {
      const res = await api.get('/claims/guides/categories')
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setCategories(Array.isArray(payload?.items) ? payload.items : [])
    } catch {
      setCategories([])
    }
  }

  const loadGuides = async () => {
    try {
      const res = await api.get('/claims/guides', { params: { categoryRootOrgId: form.categoryRootOrgId } })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const common = Array.isArray(payload?.common) ? payload.common : []
      setGuides(common)
    } catch {
      setGuides([])
    }
  }

  const handleFileUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean)
    if (files.length === 0) return
    const currentPageCount = claimDocumentsPageCount(form.documents)
    const remaining = Math.max(0, CLAIM_MAX_DOCUMENT_COUNT - currentPageCount)
    if (remaining <= 0) {
      setError(`첨부 서류는 최대 ${CLAIM_MAX_DOCUMENT_COUNT}장까지 가능합니다.`)
      return
    }
    let prepared
    try {
      prepared = await prepareClaimDocumentFiles(files, {
        initialPageCount: currentPageCount,
        maxPageCount: CLAIM_MAX_DOCUMENT_COUNT,
      })
    } catch {
      setError('PDF 페이지 수를 확인할 수 없습니다.')
      return
    }
    const acceptedFiles = prepared.accepted
    const skippedCount = prepared.skippedByPageLimit
    if (acceptedFiles.length === 0) {
      setError(`첨부 서류는 최대 ${CLAIM_MAX_DOCUMENT_COUNT}장까지 가능합니다.`)
      return
    }
    if (skippedCount > 0) {
      setError(`첨부 서류는 최대 ${CLAIM_MAX_DOCUMENT_COUNT}장까지 가능합니다. 초과 ${skippedCount}개는 제외했습니다.`)
    }
    const uploaded = []
    let failed = 0
    for (const item of acceptedFiles) {
      try {
        const { file, pageCount } = item
        const formData = new FormData()
        formData.append('file', file)
        const res = await api.post('/files/upload/claim-document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const fileId = payload?.fileId || payload?.id
        if (fileId) {
          uploaded.push({
            fileId,
            documentType: 'ETC',
            label: file.name,
            pageCount: Number(payload?.pageCount) > 0 ? Number(payload.pageCount) : pageCount,
          })
        }
      } catch {
        failed += 1
      }
    }
    if (uploaded.length > 0) {
      setForm(prev => {
        const toAdd = []
        let pages = claimDocumentsPageCount(prev.documents)
        for (const doc of uploaded) {
          const pageCount = claimDocumentPageCount(doc)
          if (pages + pageCount > CLAIM_MAX_DOCUMENT_COUNT) break
          toAdd.push({
            ...doc,
            pageCount,
            sortOrder: prev.documents.length + toAdd.length,
          })
          pages += pageCount
        }
        return {
          ...prev,
          documents: [...prev.documents, ...toAdd],
        }
      })
    }
    if (failed > 0) {
      setError(
        failed === acceptedFiles.length
          ? '파일 업로드에 실패했습니다.'
          : `${failed}개 파일 업로드에 실패했습니다. (${acceptedFiles.length - failed}개 성공)`,
      )
    }
  }

  const buildSavePayload = useCallback((status, f) => {
    const persisted = persistedExtraFields && typeof persistedExtraFields === 'object' ? { ...persistedExtraFields } : {}
    delete persisted.accidentDateHour
    delete persisted.accidentDateMinute
    delete persisted.workersCompInsuranceProcessed
    const cc = String(f.claimContent ?? '').trim()
    if (cc) persisted.claimContent = cc
    else if (!lockCommonFields) delete persisted.claimContent
    const aip = String(f.autoInsuranceProcessed ?? '').trim()
    if (aip) persisted.autoInsuranceProcessed = aip
    else if (!lockCommonFields) delete persisted.autoInsuranceProcessed
    const art = f.accidentReceiptType === 'ADDITIONAL' || f.accidentReceiptType === 'NEW' ? f.accidentReceiptType : 'NEW'
    persisted.accidentReceiptType = art
    additionalFieldDefs.forEach((field) => {
      const key = field.fieldKey
      if (!isAdditionalFieldVisible(field, additionalFieldDefs, additionalFieldValues)) {
        delete persisted[key]
        return
      }
      const raw = additionalFieldValues?.[key]
      if (field.inputType === 'CHECKBOX') {
        if (raw === 'Y' || raw === 'N') persisted[key] = raw
        else delete persisted[key]
        return
      }
      const value = raw == null ? '' : String(raw).trim()
      if (value) persisted[key] = value
      else delete persisted[key]
    })

    return {
      customerName: f.customerName.trim(),
      customerBirthDate: f.customerBirthDate || null,
      customerPhone: f.customerPhone || null,
      insurerOrgId: Number(f.insurerOrgId),
      categoryRootOrgId: f.categoryRootOrgId ? Number(f.categoryRootOrgId) : null,
      claimSection: f.claimSection || null,
      claimItem: f.claimItem || null,
      claimAmount: f.claimAmount ? parseFloat(f.claimAmount) : null,
      claimDate: f.claimDate || null,
      accidentDate: f.accidentDate || null,
      memo: f.memo || null,
      status,
      extraFields: Object.keys(persisted).length > 0 ? persisted : null,
      documents: takeClaimDocumentsWithinPageLimit(f.documents, CLAIM_MAX_DOCUMENT_COUNT),
    }
  }, [persistedExtraFields, lockCommonFields, additionalFieldDefs, additionalFieldValues])

  const saveApplication = useCallback(async (status, options = {}) => {
    const { silent = false } = options
    const f = formRef.current
    if (!f.customerName || !f.insurerOrgId) {
      if (!silent) {
        setError(status === 'DRAFT' ? '임시저장도 고객명과 보험사는 필요합니다.' : '고객명과 보험사는 필수입니다.')
        setOpenStep('2')
      }
      return
    }
    if (!silent && status !== 'DRAFT') {
      const additionalFieldError = validateRequiredAdditionalFields(additionalFieldDefs, additionalFieldValues)
      if (additionalFieldError) {
        setError(additionalFieldError)
        setOpenStep('2')
        return
      }
    }

    const payload = buildSavePayload(status, f)
    if (silent && status === 'DRAFT') {
      const hash = JSON.stringify(payload)
      if (hash === lastSilentSaveHashRef.current) return
      if (silentSaveLockRef.current) return
      silentSaveLockRef.current = true
    } else {
      setSubmitting(true)
      setSubmitMode(status)
      if (!silent) setError(null)
    }

    try {
      const draftId = effectiveDraftIdRef.current
      const res = draftId
        ? await api.put(`/claims/applications/${draftId}/content`, payload)
        : await api.post('/claims/applications', payload)
      const created = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      if (created?.id) {
        setEffectiveDraftId(created.id)
        if (status === 'DRAFT') {
          lastSilentSaveHashRef.current = JSON.stringify(buildSavePayload('DRAFT', formRef.current))
        }
      }
      if (onSaved) {
        onSaved(created, { silent })
      } else if (status === 'DRAFT' && created?.id) {
        navigate(`/support/claims/new?draftId=${created.id}`, { replace: true })
      } else {
        navigate(`/support/claims/${created.id}`)
      }
    } catch (e) {
      if (!silent) {
        const msg = e?.response?.data?.message
        setError(msg || (status === 'DRAFT' ? '임시저장에 실패했습니다.' : '청구 신청서 생성에 실패했습니다.'))
      }
    } finally {
      if (silent && status === 'DRAFT') {
        silentSaveLockRef.current = false
      } else {
        setSubmitting(false)
        setSubmitMode('SUBMITTED')
      }
    }
  }, [buildSavePayload, navigate, onSaved, additionalFieldDefs, additionalFieldValues])

  const trySilentAutoDraft = useCallback(() => {
    saveApplication('DRAFT', { silent: true })
  }, [saveApplication])

  useEffect(() => {
    if (loadingDraft) return undefined
    const id = window.setInterval(() => {
      trySilentAutoDraft()
    }, 20000)
    return () => window.clearInterval(id)
  }, [loadingDraft, trySilentAutoDraft])

  useEffect(() => {
    if (loadingDraft) return undefined
    const prev = prevOpenStepRef.current
    prevOpenStepRef.current = openStep
    if ((prev === '1' && openStep === '2') || (prev === '2' && openStep === '3')) {
      const t = window.setTimeout(() => trySilentAutoDraft(), 400)
      return () => window.clearTimeout(t)
    }
    return undefined
  }, [loadingDraft, openStep, trySilentAutoDraft])

  const documentsKey = useMemo(
    () => JSON.stringify((form.documents || []).map((d) => ({
      fileId: d?.fileId,
      pageCount: claimDocumentPageCount(d),
    }))),
    [form.documents],
  )
  const documentPageCount = claimDocumentsPageCount(form.documents)

  useEffect(() => {
    if (loadingDraft) return undefined
    if (!form.customerName?.trim() || !form.insurerOrgId) return undefined
    if ((form.documents || []).length === 0) return undefined
    const t = window.setTimeout(() => trySilentAutoDraft(), 800)
    return () => window.clearTimeout(t)
  }, [documentsKey, loadingDraft, form.customerName, form.insurerOrgId, trySilentAutoDraft])

  const handleSubmit = async (e) => {
    e.preventDefault()
    await saveApplication('SUBMITTED')
  }

  const closeOrBack = () => {
    if (onCancel) {
      onCancel()
      return
    }
    navigate('/support/claims/applications')
  }

  return (
    <div className="space-y-4">
      {loadingDraft && (
        <div className="rounded border bg-slate-50 p-3 text-sm text-slate-600">
          임시저장 청구서를 불러오는 중...
        </div>
      )}
      {!loadingDraft && (
        <div
          className={`sticky top-0 z-30 mb-3 border-b border-slate-200 bg-white pb-3 pt-1 shadow-sm ${
            embedded ? '-mx-4 px-4' : ''
          }`}
        >
          {!embedded && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h1 className="text-xl font-bold sm:text-2xl">
                {draftApplicationId
                  ? (persistedExtraFields?.reclaimedFromApplicationId ? '재청구 (이전 내용 복사)' : '임시저장 청구서 이어쓰기')
                  : '신규 청구 신청서'}
              </h1>
              <Link
                to="/support/claims/applications"
                className="shrink-0 rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
              >
                목록으로
              </Link>
            </div>
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3">
            <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-3">
              {[
                { step: '1', title: '보험사/카테고리 선택' },
                { step: '2', title: '청구 정보 입력' },
                { step: '3', title: '첨부 및 저장' },
              ].map((item) => (
                <button
                  key={item.step}
                  type="button"
                  className={`rounded border px-3 py-2 text-left transition-colors ${
                    openStep === item.step ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                  onClick={() => setOpenStep((prev) => (prev === item.step ? '' : item.step))}
                >
                  <div className="text-xs font-semibold text-slate-500">STEP {item.step}</div>
                  <div className="mt-1 font-semibold text-slate-900">{item.title}</div>
                </button>
              ))}
            </div>
            <div className="flex shrink-0 items-center justify-stretch sm:flex-col sm:justify-center sm:pt-0">
              <button
                type="button"
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50 sm:min-w-[7.5rem] sm:py-2.5"
                disabled={submitting}
                onClick={() => saveApplication('DRAFT', {})}
              >
                {submitting && submitMode === 'DRAFT' ? '임시저장 중...' : '임시저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <StepSection step="1" title="보험사/카테고리 선택" open={openStep === '1'} onToggle={() => setOpenStep(prev => (prev === '1' ? '' : '1'))}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-sm text-slate-600 mb-1">보험사 *</div>
              <select
                className="w-full rounded border px-3 py-2 text-sm disabled:bg-slate-50"
                value={form.insurerOrgId || ''}
                onChange={(e) => {
                  const v = e.target.value || null
                  const vid = v ? String(v) : ''
                  if (vid && !lockInsurer && claimTemplateByOrgId[vid] === false) return
                  setForm({ ...form, insurerOrgId: v ? Number(v) : null })
                }}
                disabled={lockInsurer}
                required
              >
                <option value="">{claimTemplateLoading ? '청구 서식 여부 확인 중…' : '선택하세요'}</option>
                {insurerOrgs.map((org) => {
                  const oid = String(org.id)
                  const noTpl = !claimTemplateLoading && claimTemplateByOrgId[oid] === false
                  const optDisabled = !lockInsurer && (claimTemplateLoading || noTpl)
                  return (
                    <option key={org.id} value={org.id} disabled={optDisabled}>
                      {org.name}{noTpl ? ' (청구 서식 미등록)' : ''}
                    </option>
                  )
                })}
              </select>
              {!!draftApplicationId && form.insurerOrgId && !claimTemplateLoading && claimTemplateByOrgId[String(form.insurerOrgId)] === false && (
                <p className="mt-2 text-xs text-amber-900 rounded border border-amber-200 bg-amber-50 px-3 py-2">
                  이 임시저장 건의 보험사는 현재 활성 청구 서식이 없습니다. PDF·팩스 발행이 되지 않을 수 있으니 운영 &gt; 서식관리 &gt; 청구서식에서 등록해 주세요.
                </p>
              )}
            </div>
            <div>
              <div className="text-sm text-slate-600 mb-1">카테고리</div>
              <select
                className="w-full rounded border px-3 py-2 text-sm disabled:bg-slate-50"
                value={form.categoryRootOrgId || ''}
                onChange={(e) => setForm({ ...form, categoryRootOrgId: e.target.value || null, claimSection: '', claimItem: '' })}
                disabled={lockCategory}
              >
                <option value="">선택하세요</option>
                {categories.map(cat => (
                  <option key={cat.orgId} value={cat.orgId}>{cat.name}</option>
                ))}
              </select>
            </div>
            {form.categoryRootOrgId && (
              <div className="md:col-span-2">
                <div className="text-sm text-slate-600 mb-1">청구 항목</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={`${form.claimSection || ''}|${form.claimItem || ''}`}
                  onChange={(e) => {
                    const [section, item] = e.target.value.split('|')
                    setForm({ ...form, claimSection: section || '', claimItem: item || '' })
                  }}
                  disabled={isLocked(initialClaimSection) || isLocked(initialClaimItem)}
                >
                  <option value="|">선택하세요</option>
                  {guideOptions.map(g => (
                    <option key={g.id} value={`${g.claimSection || ''}|${g.claimItem || ''}`}>
                      {g.claimSection} / {g.claimItem}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </StepSection>

        <StepSection step="2" title="청구 정보 입력" open={openStep === '2'} onToggle={() => setOpenStep(prev => (prev === '2' ? '' : '2'))}>
          {lockCommonFields ? (
            <>
              <div className="rounded border border-sky-200 bg-sky-50/90 px-3 py-2 text-sm text-sky-950 leading-relaxed">
                고객·청구 공통 항목은 <span className="font-semibold">청구 플로우 STEP 2</span>에서 이미 반영되었습니다.
                보험사별로 추가로 입력할 항목이 없으면 이 단계에서 입력할 내용이 없습니다.
              </div>
              <ClaimAdditionalFieldInputs
                fields={additionalFieldDefs}
                values={additionalFieldValues}
                onChange={setAdditionalFieldValues}
                className="mt-4"
              />
            </>
          ) : (
            <>
              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start">
                <div>
                  <div className="text-sm text-slate-600 mb-2">접수 구분</div>
                  <ClaimReceiptTypePick
                    idPrefix="claim-app-receipt"
                    value={form.accidentReceiptType === 'ADDITIONAL' ? 'ADDITIONAL' : 'NEW'}
                    onChange={(v) => setForm((prev) => ({ ...prev, accidentReceiptType: v }))}
                  />
                </div>
                <div>
                  <div className="text-sm text-slate-600 mb-1">청구 일자</div>
                  <input
                    type="date"
                    className="w-full rounded border px-3 py-2 text-sm"
                    max={todayYmdLocal()}
                    value={form.claimDate}
                    onChange={(e) => {
                      if (isLocked(initialClaimDate)) return
                      const v = e.target.value
                      const m = todayYmdLocal()
                      if (v && v > m) return
                      setForm({ ...form, claimDate: v })
                    }}
                    disabled={isLocked(initialClaimDate)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <div className="text-sm text-slate-600 mb-1">고객명 *</div>
                  <ManagedCustomerAutocomplete
                    value={form.customerName}
                    onValueChange={(next) => setForm({ ...form, customerRefId: '', customerName: next })}
                    selectedCustomerId={form.customerRefId}
                    onSelectCustomer={(customer) => setForm((prev) => ({
                      ...prev,
                      customerRefId: String(customer.id || ''),
                      customerName: customer.customerName || prev.customerName,
                      customerBirthDate: isLocked(initialCustomerBirthDate)
                        ? prev.customerBirthDate
                        : (customer.birthDate ? String(customer.birthDate).slice(0, 10) : prev.customerBirthDate),
                      customerPhone: isLocked(initialCustomerPhone)
                        ? prev.customerPhone
                        : (customer.customerPhone ? String(customer.customerPhone).replace(/\D/g, '') : prev.customerPhone),
                    }))}
                    onClearCustomer={() => setForm((prev) => ({ ...prev, customerRefId: '' }))}
                    inputClassName="w-full rounded border px-3 py-2 text-sm"
                    disabled={isLocked(initialCustomerName)}
                    required
                  />
                </div>
                <div>
                  <div className="text-sm text-slate-600 mb-1">생년월일</div>
                  <input
                    type="date"
                    className="w-full rounded border px-3 py-2 text-sm"
                    value={form.customerBirthDate}
                    onChange={(e) => setForm({ ...form, customerBirthDate: e.target.value })}
                    disabled={isLocked(initialCustomerBirthDate)}
                  />
                </div>
                <div>
                  <div className="text-sm text-slate-600 mb-1">연락처</div>
                  <PhoneDigitsSplitInput
                    idPrefix="claim-app-ph"
                    value={form.customerPhone}
                    onChange={(v) => setForm({ ...form, customerRefId: '', customerPhone: v })}
                    disabled={isLocked(initialCustomerPhone)}
                  />
                </div>
                <div>
                  <div className="text-sm text-slate-600 mb-1">청구 금액</div>
                  <input
                    type="number"
                    className="w-full rounded border px-3 py-2 text-sm"
                    value={form.claimAmount}
                    onChange={(e) => setForm({ ...form, claimAmount: e.target.value })}
                    disabled={isLocked(initialClaimAmount)}
                  />
                </div>
                <div>
                  <div className="text-sm text-slate-600 mb-1">사고/진단 일자</div>
                  <input
                    type="date"
                    className="w-full rounded border px-3 py-2 text-sm"
                    max={todayYmdLocal()}
                    value={form.accidentDate}
                    onChange={(e) => {
                      if (isLocked(initialAccidentDate)) return
                      const v = e.target.value
                      const m = todayYmdLocal()
                      if (v && v > m) return
                      setForm({ ...form, accidentDate: v })
                    }}
                    disabled={isLocked(initialAccidentDate)}
                  />
                </div>
              </div>
              <div className="mt-4 max-w-xl">
                <div className="text-sm text-slate-600 mb-1">자동차보험 처리 여부</div>
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={form.autoInsuranceProcessed}
                  onChange={(e) => setForm({ ...form, autoInsuranceProcessed: e.target.value })}
                >
                  <option value="">선택</option>
                  <option value="예">예</option>
                  <option value="아니오">아니오</option>
                </select>
              </div>
              <div className="mt-3">
                <div className="text-sm text-slate-600 mb-1">메모</div>
                <textarea
                  className="w-full rounded border px-3 py-2 text-sm"
                  rows={4}
                  value={form.memo}
                  onChange={(e) => setForm({ ...form, memo: e.target.value })}
                  disabled={isLocked(initialMemo)}
                />
              </div>
              <ClaimAdditionalFieldInputs
                fields={additionalFieldDefs}
                values={additionalFieldValues}
                onChange={setAdditionalFieldValues}
                className="mt-4"
              />
            </>
          )}
        </StepSection>

        <StepSection step="3" title="첨부 및 저장" open={openStep === '3'} onToggle={() => setOpenStep(prev => (prev === '3' ? '' : '3'))}>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`rounded border px-3 py-1.5 text-sm font-semibold ${
              documentPageCount >= CLAIM_MAX_DOCUMENT_COUNT
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-slate-200 bg-slate-50 text-slate-600'
            }`}>
              {documentPageCount} / {CLAIM_MAX_DOCUMENT_COUNT}장
            </span>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              className="text-sm"
              onChange={(e) => {
                const files = e.target.files
                if (files?.length) handleFileUpload(files)
                e.target.value = ''
              }}
              disabled={documentPageCount >= CLAIM_MAX_DOCUMENT_COUNT}
            />
          </div>
          {form.documents.length > 0 && (
            <div className="mt-3 space-y-2">
              {form.documents.map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between rounded border p-2">
                  <span className="text-sm">{doc.label} · {claimDocumentPageCount(doc)}장</span>
                  <button
                    type="button"
                    className="text-red-600 text-sm hover:text-red-800"
                    onClick={() => {
                      setForm(prev => ({
                        ...prev,
                        documents: prev.documents.filter((_, i) => i !== idx),
                      }))
                    }}
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
              onClick={closeOrBack}
            >
              취소
            </button>
            <button
              type="submit"
              className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              disabled={submitting}
            >
              {submitting && submitMode === 'SUBMITTED' ? '저장 중...' : '저장'}
            </button>
          </div>
        </StepSection>
      </form>
    </div>
  )
}

export default function ClaimApplicationNewPage() {
  const [searchParams] = useSearchParams()
  const draftIdFromUrl = searchParams.get('draftId')
  const insurerOrgIdFromUrl = searchParams.get('insurerOrgId')
  const categoryRootOrgIdFromUrl = searchParams.get('categoryRootOrgId')

  return (
    <ClaimApplicationForm
      draftApplicationId={draftIdFromUrl ? Number(draftIdFromUrl) : null}
      initialInsurerOrgId={insurerOrgIdFromUrl ? Number(insurerOrgIdFromUrl) : null}
      initialCategoryRootOrgId={categoryRootOrgIdFromUrl ? Number(categoryRootOrgIdFromUrl) : null}
      lockInsurer={!!insurerOrgIdFromUrl && !draftIdFromUrl}
      lockCategory={!!categoryRootOrgIdFromUrl && !draftIdFromUrl}
    />
  )
}
